import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, 
  Building2, 
  Eye, 
  Calendar,
  AlertTriangle,
  Clock,
  TrendingUp,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { api } from '../../utils/api';

const DueDetails = () => {
  const { officeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [officeData, setOfficeData] = useState(null);
  const [detailsData, setDetailsData] = useState({ drivers: [], vendors: [] });
  const [activeTab, setActiveTab] = useState('drivers');
  
  // Table state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [dueFilter, setDueFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('overdue');

  useEffect(() => {
    if (officeId) {
      fetchOfficeDetails(officeId);
    }
  }, [officeId, currentPage, searchTerm, dueFilter, sortBy, activeTab]);

  const fetchOfficeDetails = async (officeId) => {
    try {
      setLoading(true);
      const response = await api.get(`/payments/due/office/${officeId}`, {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          status: dueFilter,
          sortBy: sortBy,
          type: activeTab
        }
      });
      setDetailsData(response.data);
      
      // Also fetch office info
      const officeResponse = await api.get(`/regional-offices/${officeId}`);
      setOfficeData(officeResponse.data);
    } catch (error) {
      console.error('Error fetching office details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDueStatus = (nextDueDate) => {
    const now = Date.now();
    const dueDate = new Date(nextDueDate).getTime();
    const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    
    if (dueDate < now) {
      return { status: 'overdue', text: 'Overdue', color: 'text-red-600 bg-red-100' };
    } else if (diffDays === 0) {
      return { status: 'today', text: 'Due Today', color: 'text-orange-600 bg-orange-100' };
    } else {
      return { status: 'pending', text: `Due in ${diffDays} days`, color: 'text-blue-600 bg-blue-100' };
    }
  };

  const getPaginatedData = () => {
    const data = activeTab === 'drivers' ? detailsData.drivers : detailsData.vendors;
    return {
      data: data,
      totalItems: detailsData.totalItems || 0,
      totalPages: detailsData.totalPages || 0
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDriverClick = (driverId) => {
    navigate(`/drivers/${driverId}`);
  };

  const handleTripClick = (tripId) => {
    navigate(`/trips/${tripId}`);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/payments/due')}
          className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Payment Details - {officeData?.name || 'Regional Office'}
          </h1>
          <p className="text-gray-600">{officeData?.address}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('drivers');
              setCurrentPage(1);
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'drivers'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Drivers ({detailsData.drivers.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('vendors');
              setCurrentPage(1);
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'vendors'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Vendors ({detailsData.vendors.length})
          </button>
        </nav>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 w-full"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={dueFilter}
              onChange={(e) => {
                setDueFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="overdue">Overdue</option>
              <option value="today">Due Today</option>
              <option value="pending">Due in X Days</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="overdue">Sort by Overdue</option>
              <option value="amount">Sort by Amount</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {activeTab === 'drivers' ? 'Driver' : 'Vendor'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getPaginatedData().data.map((item) => (
                <tr key={item._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        activeTab === 'drivers' ? 'bg-blue-100' : 'bg-green-100'
                      }`}>
                        {activeTab === 'drivers' ? (
                          <Users className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Building2 className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => handleDriverClick(item._id)}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-900 cursor-pointer"
                        >
                          {item.name}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {activeTab === 'drivers' ? item.phone : item.contactInfo?.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(activeTab === 'drivers' ? item.driverDue : item.vendorDue)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDueStatus(item.nextDueDate).color}`}>
                      {getDueStatus(item.nextDueDate).text}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          if (activeTab === 'drivers') {
                            navigate(`/payments/due/driver/${item._id}/trips`);
                          } else {
                            navigate(`/payments/due/vendor/${item._id}/trips`);
                          }
                        }}
                        className="text-indigo-600 hover:text-indigo-900 flex items-center"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Trips
                      </button>
                      <button
                        onClick={() => {
                          if (activeTab === 'drivers') {
                            navigate(`/drivers/${item._id}`);
                          } else {
                            navigate(`/vendors/${item._id}`);
                          }
                        }}
                        className="text-green-600 hover:text-green-900 flex items-center"
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {getPaginatedData().totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, getPaginatedData().totalItems)} of {getPaginatedData().totalItems} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                
                {Array.from({ length: getPaginatedData().totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm rounded-md ${
                      currentPage === page
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getPaginatedData().totalPages))}
                  disabled={currentPage === getPaginatedData().totalPages}
                  className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default DueDetails;
